import React, { useState, useEffect, useMemo } from 'react';
import { Timestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isAfter, isBefore, startOfDay, endOfDay, subDays, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import AdminLayout from '../../../components/layout/AdminLayout';
import DatePicker from '../../../components/ui/DatePicker';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Table from '../../../components/ui/Table';
import Spinner from '../../../components/ui/Spinner';
import Card from '../../../components/ui/Card';
import Alert from '../../../components/ui/Alert';
import DataChart from '../../../components/shared/DataChart';
import Badge from '../../../components/ui/Badge';
import Input from '../../../components/ui/Input';
import Toggle from '../../../components/ui/Toggle';

import { useFirestoreQuery } from '../../../hooks/useFirestoreQuery';
import { ITransacao as Transacao, ICliente as Cliente } from '../../../types/firestore.types';
import { formatarMoeda as formatCurrency } from '../../../utils/currencyUtils';

interface RelatorioTransacaoExibicao extends Transacao {
  nome_cliente: string;
  nome_unidade: string;
  data_hora_formatada: string;
  valor_formatado: string;
  status_color: string;
  tipo_color: string;
  categoria_transacao: string;
  origem_destino: string;
  descricao_detalhada: string;
  fluxo_caixa: 'entrada' | 'saida' | 'neutro';
}

// Unidades fixas do sistema
const UNIDADES = [
  { id: '1', nome: 'Mr. Tugas Aeroporto' },
  { id: '2', nome: 'Mr. Tugas Cervejaria' },
  { id: '3', nome: 'Mr. Tugas Jardim Norte' },
];

// PERÍODOS PRÉ-DEFINIDOS
const PERIODOS_PREDEFINIDOS = [
  { id: 'hoje', label: 'Hoje', dias: 0 },
  { id: 'ontem', label: 'Ontem', dias: 1 },
  { id: '7_dias', label: 'Últimos 7 dias', dias: 7 },
  { id: '15_dias', label: 'Últimos 15 dias', dias: 15 },
  { id: '30_dias', label: 'Últimos 30 dias', dias: 30 },
  { id: '60_dias', label: 'Últimos 60 dias', dias: 60 },
  { id: '90_dias', label: 'Últimos 90 dias', dias: 90 },
  { id: 'personalizado', label: 'Personalizado', dias: null },
];

// 🆕 CONFIGURAÇÃO DE PAGINAÇÃO
const ITENS_POR_PAGINA_OPCOES = [10, 25, 50, 100];

const ReportsPage: React.FC = () => {
  // Estados principais
  const [periodoPredefinido, setPeriodoPredefinido] = useState<string>('30_dias');
  const [dataInicio, setDataInicio] = useState<Date | null>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [dataFim, setDataFim] = useState<Date | null>(new Date());
  const [unidadeSelecionadaId, setUnidadeSelecionadaId] = useState<string>('');
  const [clienteSelecionadoId, setClienteSelecionadoId] = useState<string>('');
  const [tipoOperacao, setTipoOperacao] = useState<string>('');
  const [statusTransacao, setStatusTransacao] = useState<string>('');
  
  // NOVOS FILTROS
  const [valorMinimo, setValorMinimo] = useState<string>('');
  const [valorMaximo, setValorMaximo] = useState<string>('');
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<string>('');
  const [fluxoCaixaSelecionado, setFluxoCaixaSelecionado] = useState<string>('');
  const [buscarTexto, setBuscarTexto] = useState<string>('');
  const [mostrarFiltrosAvancados, setMostrarFiltrosAvancados] = useState<boolean>(false);
  const [filtrosAplicados, setFiltrosAplicados] = useState<number>(0);

  // 🆕 ESTADOS DE PAGINAÇÃO
  const [paginaAtual, setPaginaAtual] = useState<number>(1);
  const [itensPorPagina, setItensPorPagina] = useState<number>(25);

  const [relatorioDados, setRelatorioDados] = useState<RelatorioTransacaoExibicao[] | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState<boolean>(false);
  const [errorReport, setErrorReport] = useState<string | null>(null);

  // Buscar dados do Firestore
  const { dados: clientes, carregando: carregandoClientes } = useFirestoreQuery<Cliente>('clientes');
  const { dados: todasTransacoes, carregando: carregandoTransacoes } = useFirestoreQuery<Transacao>('transacoes');

  // 🎯 FUNÇÃO AUXILIAR PARA OBTER DATA CORRETA - USANDO createdAt PARA CASHBACK E PIX
  const obterDataTransacao = (transacao: Transacao): Date => {
    try {
      // PRIORIDADE 1: Sempre usar createdAt quando disponível
      if (transacao.createdAt) {
        if (typeof transacao.createdAt === 'object' && 'toDate' in transacao.createdAt) {
          return (transacao.createdAt as Timestamp).toDate();
        }
        if (transacao.createdAt instanceof Date) {
          return transacao.createdAt;
        }
        if (typeof transacao.createdAt === 'string') {
          const date = new Date(transacao.createdAt);
          if (!isNaN(date.getTime())) return date;
        }
        if (typeof transacao.createdAt === 'number') {
          return new Date(transacao.createdAt);
        }
      }

      // PRIORIDADE 2: validatedAt para PIX (se não tiver createdAt)
      if (transacao.validatedAt) {
        if (typeof transacao.validatedAt === 'object' && 'toDate' in transacao.validatedAt) {
          return (transacao.validatedAt as Timestamp).toDate();
        }
        if (transacao.validatedAt instanceof Date) {
          return transacao.validatedAt;
        }
      }

      // PRIORIDADE 3: criado_em (fallback)
      if (transacao.criado_em && typeof transacao.criado_em === 'object' && 'toDate' in transacao.criado_em) {
        return (transacao.criado_em as Timestamp).toDate();
      }
      
      if (transacao.data_hora && typeof transacao.data_hora === 'object' && 'toDate' in transacao.data_hora) {
        return (transacao.data_hora as Timestamp).toDate();
      }
      
      // Verificar se já é um objeto Date
      if (transacao.criado_em instanceof Date) {
        return transacao.criado_em;
      }
      
      if (transacao.data_hora instanceof Date) {
        return transacao.data_hora;
      }
      
      // Verificar se é string ISO
      if (typeof transacao.criado_em === 'string') {
        const date = new Date(transacao.criado_em);
        if (!isNaN(date.getTime())) return date;
      }
      
      if (typeof transacao.data_hora === 'string') {
        const date = new Date(transacao.data_hora);
        if (!isNaN(date.getTime())) return date;
      }
      
      // Se for número (timestamp)
      if (typeof transacao.criado_em === 'number') {
        return new Date(transacao.criado_em);
      }
      
      if (typeof transacao.data_hora === 'number') {
        return new Date(transacao.data_hora);
      }
      
      // Fallback para data atual
      console.warn('Não foi possível determinar a data para transação:', transacao.transactionId, 'Usando data atual.');
      return new Date();
    } catch (error) {
      console.error('Erro ao converter data da transação:', transacao.transactionId, error);
      return new Date();
    }
  };

  // 🆕 CÁLCULOS DE PAGINAÇÃO
  const dadosPaginados = useMemo(() => {
    if (!relatorioDados) return [];
    
    const inicio = (paginaAtual - 1) * itensPorPagina;
    const fim = inicio + itensPorPagina;
    return relatorioDados.slice(inicio, fim);
  }, [relatorioDados, paginaAtual, itensPorPagina]);

  const totalPaginas = useMemo(() => {
    if (!relatorioDados) return 0;
    return Math.ceil(relatorioDados.length / itensPorPagina);
  }, [relatorioDados, itensPorPagina]);

  const inicioItem = useMemo(() => {
    return (paginaAtual - 1) * itensPorPagina + 1;
  }, [paginaAtual, itensPorPagina]);

  const fimItem = useMemo(() => {
    if (!relatorioDados) return 0;
    return Math.min(paginaAtual * itensPorPagina, relatorioDados.length);
  }, [paginaAtual, itensPorPagina, relatorioDados]);

  // 🆕 FUNÇÕES DE PAGINAÇÃO
  const irParaPagina = (pagina: number) => {
    setPaginaAtual(Math.max(1, Math.min(pagina, totalPaginas)));
  };

  const avancarPagina = () => {
    if (paginaAtual < totalPaginas) {
      setPaginaAtual(paginaAtual + 1);
    }
  };

  const voltarPagina = () => {
    if (paginaAtual > 1) {
      setPaginaAtual(paginaAtual - 1);
    }
  };

  // 🆕 RESETAR PAGINAÇÃO QUANDO MUDAR FILTROS
  useEffect(() => {
    setPaginaAtual(1);
  }, [
    dataInicio, dataFim, unidadeSelecionadaId, clienteSelecionadoId, 
    tipoOperacao, statusTransacao, valorMinimo, valorMaximo, 
    categoriaSelecionada, fluxoCaixaSelecionado, buscarTexto
  ]);

  // ATUALIZAR PERÍODO QUANDO PERÍODO PRÉ-DEFINIDO MUDAR
  useEffect(() => {
    if (periodoPredefinido !== 'personalizado') {
      const periodo = PERIODOS_PREDEFINIDOS.find(p => p.id === periodoPredefinido);
      if (periodo) {
        const dataFim = new Date();
        const dataInicio = subDays(dataFim, periodo.dias);
        setDataInicio(dataInicio);
        setDataFim(dataFim);
      }
    }
  }, [periodoPredefinido]);

  // CALCULAR QUANTIDADE DE FILTROS APLICADOS
  useEffect(() => {
    let count = 0;
    if (unidadeSelecionadaId) count++;
    if (clienteSelecionadoId) count++;
    if (tipoOperacao) count++;
    if (statusTransacao) count++;
    if (valorMinimo) count++;
    if (valorMaximo) count++;
    if (categoriaSelecionada) count++;
    if (fluxoCaixaSelecionado) count++;
    if (buscarTexto) count++;
    setFiltrosAplicados(count);
  }, [
    unidadeSelecionadaId, clienteSelecionadoId, tipoOperacao, statusTransacao,
    valorMinimo, valorMaximo, categoriaSelecionada, fluxoCaixaSelecionado, buscarTexto
  ]);

  // Opções para selects
  const opcoesUnidades = useMemo(() => [
    { valor: '', label: 'Todas as Unidades' },
    ...UNIDADES.map(u => ({ valor: u.id, label: u.nome }))
  ], []);

  const opcoesClientes = useMemo(() => [
    { valor: '', label: 'Todos os Clientes' },
    ...(clientes?.map(c => ({ valor: c.id, label: c.nome })) || [])
  ], [clientes]);

  const opcoesTipoOperacao = useMemo(() => [
    { valor: '', label: 'Todos os Tipos' },
    { valor: 'credito', label: 'Crédito (Recargas)' },
    { valor: 'debito', label: 'Débito (Pagamentos)' },
  ], []);

  const opcoesStatus = useMemo(() => [
    { valor: '', label: 'Todos os Status' },
    { valor: 'idle', label: 'Aguardando Início' },
    { valor: 'requesting', label: 'Solicitando' },
    { valor: 'pending', label: 'Pendente' },
    { valor: 'pendente_confirmacao', label: 'Pendente Confirmação' },
    { valor: 'validating', label: 'Validando' },
    { valor: 'completed', label: 'Concluído' },
    { valor: 'confirmed', label: 'Confirmado' },
    { valor: 'failed', label: 'Falhou' },
    { valor: 'expired', label: 'Expirado' },
  ], []);

  // NOVAS OPÇÕES DE FILTRO
  const opcoesCategoria = useMemo(() => [
    { valor: '', label: 'Todas as Categorias' },
    { valor: 'Recebimento PIX', label: 'Recebimento PIX' },
    { valor: 'Cashback', label: 'Cashback' },
    { valor: 'Pagamento Cliente', label: 'Pagamento Cliente' },
    { valor: 'Recarga Manual', label: 'Recarga Manual' },
    { valor: 'Estorno', label: 'Estorno' },
  ], []);

  const opcoesFluxoCaixa = useMemo(() => [
    { valor: '', label: 'Todos os Fluxos' },
    { valor: 'entrada', label: 'Entrada' },
    { valor: 'saida', label: 'Saída' },
    { valor: 'neutro', label: 'Mov. Interna' },
  ], []);

  const opcoesPeriodo = useMemo(() => 
    PERIODOS_PREDEFINIDOS.map(p => ({
      valor: p.id,
      label: p.label
    }))
  , []);

  // 🆕 OPÇÕES DE ITENS POR PÁGINA
  const opcoesItensPorPagina = useMemo(() => 
    ITENS_POR_PAGINA_OPCOES.map(num => ({
      valor: num.toString(),
      label: `${num} itens`
    }))
  , []);

  // FUNÇÃO PARA APLICAR FILTROS AVANÇADOS
  const transacoesFiltradas = useMemo(() => {
    if (!todasTransacoes) return [];

    let transacoesFiltradas = [...todasTransacoes];

    // Filtro por data
    if (dataInicio) {
      const inicio = startOfDay(dataInicio);
      transacoesFiltradas = transacoesFiltradas.filter(transacao => {
        const dataTransacao = obterDataTransacao(transacao);
        return isAfter(dataTransacao, inicio) || dataTransacao.getTime() === inicio.getTime();
      });
    }

    if (dataFim) {
      const fim = endOfDay(dataFim);
      transacoesFiltradas = transacoesFiltradas.filter(transacao => {
        const dataTransacao = obterDataTransacao(transacao);
        return isBefore(dataTransacao, fim) || dataTransacao.getTime() === fim.getTime();
      });
    }

    // Filtro por unidade
    if (unidadeSelecionadaId) {
      transacoesFiltradas = transacoesFiltradas.filter(transacao => 
        transacao.unidade_id === unidadeSelecionadaId
      );
    }

    // Filtro por cliente
    if (clienteSelecionadoId) {
      transacoesFiltradas = transacoesFiltradas.filter(transacao => 
        transacao.cliente_id === clienteSelecionadoId || transacao.userId === clienteSelecionadoId
      );
    }

    // Filtro por tipo de operação
    if (tipoOperacao) {
      transacoesFiltradas = transacoesFiltradas.filter(transacao => 
        transacao.tipo_operacao === tipoOperacao
      );
    }

    // Filtro por status
    if (statusTransacao) {
      transacoesFiltradas = transacoesFiltradas.filter(transacao => 
        transacao.status === statusTransacao
      );
    }

    // FILTRO POR VALOR MÍNIMO
    if (valorMinimo) {
      const valorMin = parseFloat(valorMinimo);
      if (!isNaN(valorMin)) {
        transacoesFiltradas = transacoesFiltradas.filter(transacao => 
          (transacao.valor || 0) >= valorMin
        );
      }
    }

    // FILTRO POR VALOR MÁXIMO
    if (valorMaximo) {
      const valorMax = parseFloat(valorMaximo);
      if (!isNaN(valorMax)) {
        transacoesFiltradas = transacoesFiltradas.filter(transacao => 
          (transacao.valor || 0) <= valorMax
        );
      }
    }

    // FILTRO POR BUSCA DE TEXTO (cliente, descrição, etc)
    if (buscarTexto) {
      const termo = buscarTexto.toLowerCase();
      transacoesFiltradas = transacoesFiltradas.filter(transacao => {
        const cliente = clientes?.find(c => c.id === (transacao.cliente_id || transacao.userId));
        const nomeCliente = cliente?.nome?.toLowerCase() || '';
        const descricao = transacao.descricao?.toLowerCase() || '';
        const transactionId = transacao.transactionId?.toLowerCase() || '';
        
        return nomeCliente.includes(termo) || 
               descricao.includes(termo) || 
               transactionId.includes(termo) ||
               (transacao.numero_comanda && transacao.numero_comanda.toString().includes(termo));
      });
    }

    return transacoesFiltradas;
  }, [
    todasTransacoes, dataInicio, dataFim, unidadeSelecionadaId, 
    clienteSelecionadoId, tipoOperacao, statusTransacao, valorMinimo, 
    valorMaximo, buscarTexto, clientes
  ]);

  // FUNÇÕES AUXILIARES
  const determinarCorStatus = (status: string): string => {
    switch (status) {
      case 'completed':
      case 'confirmed':
        return 'success';
      case 'pending':
      case 'pendente_confirmacao':
      case 'validating':
        return 'warning';
      case 'requesting':
        return 'info';
      case 'failed':
      case 'expired':
        return 'error';
      case 'idle':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const determinarLabelStatus = (status: string): string => {
    switch (status) {
      case 'idle': return 'Aguardando Início';
      case 'requesting': return 'Solicitando';
      case 'pending': return 'Pendente';
      case 'pendente_confirmacao': return 'Pendente Confirmação';
      case 'validating': return 'Validando';
      case 'completed': return 'Concluído';
      case 'confirmed': return 'Confirmado';
      case 'failed': return 'Falhou';
      case 'expired': return 'Expirado';
      default: return status || 'Desconhecido';
    }
  };

  const determinarCategoria = (transacao: Transacao): string => {
    if (transacao.tipo_operacao === 'debito') return 'Pagamento Cliente';
    switch (transacao.tipo) {
      case 'cashback': return 'Cashback';
      case 'pix_payment': return 'Recebimento PIX';
      case 'recarga': return 'Recarga Manual';
      case 'estorno': return 'Estorno';
      default: return transacao.tipo_operacao === 'credito' ? 'Crédito' : 'Outro';
    }
  };

  const determinarOrigemDestino = (transacao: Transacao): string => {
    if (transacao.tipo === 'pix_payment' && transacao.merchantInfo) {
      if (typeof transacao.merchantInfo === 'string') return transacao.merchantInfo;
      if (transacao.merchantInfo.nome) return transacao.merchantInfo.nome;
    }
    if (transacao.tipo === 'cashback') return 'Sistema Cashback';
    if (transacao.unidade_id) {
      const unidade = UNIDADES.find(u => u.id === transacao.unidade_id);
      return unidade?.nome || 'Unidade Presencial';
    }
    return 'Sistema';
  };

  const gerarDescricaoDetalhada = (transacao: Transacao): string => {
    const categoria = determinarCategoria(transacao);
    switch (categoria) {
      case 'Recebimento PIX':
        return `Recebimento via PIX${transacao.merchantInfo && typeof transacao.merchantInfo === 'object' && transacao.merchantInfo.cidade ? ` - ${transacao.merchantInfo.cidade}` : ''}`;
      case 'Cashback':
        return `Cashback${transacao.initialBalance ? ` | Saldo anterior: ${formatCurrency(transacao.initialBalance)}` : ''}`;
      case 'Pagamento Cliente':
        return `Pagamento${transacao.numero_comanda ? ` - Comanda ${transacao.numero_comanda}` : ''}`;
      default:
        return categoria;
    }
  };

  const determinarFluxoCaixa = (transacao: Transacao): 'entrada' | 'saida' | 'neutro' => {
    if (transacao.tipo_operacao === 'credito') {
      if (transacao.tipo === 'pix_payment') return 'entrada';
      if (transacao.tipo === 'cashback') return 'neutro';
      return 'entrada';
    } else if (transacao.tipo_operacao === 'debito') {
      return 'saida';
    }
    return 'neutro';
  };

  // Gerar relatório com filtros aplicados
  const handleGerarRelatorio = async () => {
    setIsLoadingReport(true);
    setErrorReport(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const dadosFormatados: RelatorioTransacaoExibicao[] = transacoesFiltradas.map((transacao) => {
        const unidade = UNIDADES.find((u) => u.id === transacao.unidade_id);
        const cliente = clientes?.find((c) => c.id === (transacao.cliente_id || transacao.userId));

        const statusColor = determinarCorStatus(transacao.status || '');
        const tipoColor = transacao.tipo_operacao === 'credito' ? 'success' : 'primary';
        
        // 🎯 USAR NOVA FUNÇÃO PARA OBTER DATA CORRETA (createdAt para cashback e PIX)
        const dataTransacao = obterDataTransacao(transacao);

        const categoria = determinarCategoria(transacao);
        const origemDestino = determinarOrigemDestino(transacao);
        const descricaoDetalhada = gerarDescricaoDetalhada(transacao);
        const fluxoCaixa = determinarFluxoCaixa(transacao);

        return {
          ...transacao,
          nome_cliente: cliente?.nome || 'Cliente Não Identificado',
          nome_unidade: unidade?.nome || (transacao.tipo === 'pix_payment' ? 'Recebimento PIX' : 'Sistema Cashback'),
          data_hora_formatada: format(dataTransacao, 'dd/MM/yyyy HH:mm', { locale: ptBR }),
          valor_formatado: formatCurrency(transacao.valor || 0),
          status_color: statusColor,
          tipo_color: tipoColor,
          categoria_transacao: categoria,
          origem_destino: origemDestino,
          descricao_detalhada: descricaoDetalhada,
          fluxo_caixa: fluxoCaixa,
        };
      });

      // APLICAR FILTROS DE CATEGORIA E FLUXO DE CAIXA APÓS A FORMATAÇÃO
      let dadosFinais = dadosFormatados;

      if (categoriaSelecionada) {
        dadosFinais = dadosFinais.filter(t => t.categoria_transacao === categoriaSelecionada);
      }

      if (fluxoCaixaSelecionado) {
        dadosFinais = dadosFinais.filter(t => t.fluxo_caixa === fluxoCaixaSelecionado);
      }

      // Ordenar por data (mais recente primeiro)
      dadosFinais.sort((a, b) => {
        const dataA = obterDataTransacao(a);
        const dataB = obterDataTransacao(b);
        return dataB.getTime() - dataA.getTime();
      });

      setRelatorioDados(dadosFinais);
      setPaginaAtual(1); // Resetar para primeira página ao gerar novo relatório
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      setErrorReport('Não foi possível gerar o relatório. Tente novamente.');
    } finally {
      setIsLoadingReport(false);
    }
  };

  // FUNÇÃO handleExportCSV
  const handleExportCSV = () => {
    if (!relatorioDados) return;

    const headers = ['Data/Hora', 'Cliente', 'Categoria', 'Origem/Destino', 'Valor', 'Status', 'Fluxo', 'Descrição'];
    const csvContent = [
      headers.join(','),
      ...relatorioDados.map(t => [
        t.data_hora_formatada,
        `"${t.nome_cliente}"`,
        `"${t.categoria_transacao}"`,
        `"${t.origem_destino}"`,
        t.valor,
        determinarLabelStatus(t.status || ''),
        t.fluxo_caixa,
        `"${t.descricao_detalhada}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio_transacoes_${format(new Date(), 'dd-MM-yyyy')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // FUNÇÃO PARA LIMPAR TODOS OS FILTROS
  const handleLimparFiltros = () => {
    setPeriodoPredefinido('30_dias');
    setUnidadeSelecionadaId('');
    setClienteSelecionadoId('');
    setTipoOperacao('');
    setStatusTransacao('');
    setValorMinimo('');
    setValorMaximo('');
    setCategoriaSelecionada('');
    setFluxoCaixaSelecionado('');
    setBuscarTexto('');
    setMostrarFiltrosAvancados(false);
    setPaginaAtual(1);
  };

  // FUNÇÃO PARA LIMPAR FILTROS AVANÇADOS
  const handleLimparFiltrosAvancados = () => {
    setValorMinimo('');
    setValorMaximo('');
    setCategoriaSelecionada('');
    setFluxoCaixaSelecionado('');
    setBuscarTexto('');
    setPaginaAtual(1);
  };

  // Gerar relatório automaticamente quando os dados carregarem
  useEffect(() => {
    if (todasTransacoes && clientes) {
      handleGerarRelatorio();
    }
  }, [todasTransacoes, clientes]);

  // Dados para gráficos
  const chartData = useMemo(() => {
    if (!relatorioDados || relatorioDados.length === 0) return [];

    const dailyTotals: { [key: string]: number } = {};
    relatorioDados.filter(t => t.fluxo_caixa === 'entrada').forEach(t => {
      const dataTransacao = obterDataTransacao(t);
      const dateKey = format(dataTransacao, 'dd/MM/yyyy');
      dailyTotals[dateKey] = (dailyTotals[dateKey] || 0) + (t.valor || 0);
    });
    
    return Object.keys(dailyTotals)
      .sort((a, b) => {
        const [dA, mA, yA] = a.split('/').map(Number);
        const [dB, mB, yB] = b.split('/').map(Number);
        return new Date(yA, mA - 1, dA).getTime() - new Date(yB, mB - 1, dB).getTime();
      })
      .map(date => ({
        name: date,
        valor: dailyTotals[date],
      }));
  }, [relatorioDados]);

  // DADOS PARA GRÁFICO DE STATUS
  const statusChartData = useMemo(() => {
    if (!relatorioDados || relatorioDados.length === 0) return [];

    const statusCount: { [key: string]: number } = {};
    relatorioDados.forEach(t => {
      const status = t.status || 'unknown';
      statusCount[status] = (statusCount[status] || 0) + 1;
    });

    return Object.keys(statusCount).map(status => ({
      name: determinarLabelStatus(status),
      value: statusCount[status],
    }));
  }, [relatorioDados]);

  // Dados para gráfico de pizza por categoria
  const pieChartData = useMemo(() => {
    if (!relatorioDados || relatorioDados.length === 0) return [];

    const recebimentosPix = relatorioDados
      .filter(t => t.categoria_transacao === 'Recebimento PIX')
      .reduce((sum, t) => sum + (t.valor || 0), 0);

    const cashbacks = relatorioDados
      .filter(t => t.categoria_transacao === 'Cashback')
      .reduce((sum, t) => sum + (t.valor || 0), 0);

    const pagamentos = relatorioDados
      .filter(t => t.categoria_transacao === 'Pagamento Cliente')
      .reduce((sum, t) => sum + (t.valor || 0), 0);

    return [
      { name: 'Recebimentos PIX', value: recebimentosPix },
      { name: 'Cashbacks', value: cashbacks },
      { name: 'Pagamentos', value: pagamentos },
    ];
  }, [relatorioDados]);

  // ESTATÍSTICAS ATUALIZADAS PARA FLUXO REAL
  const estatisticas = useMemo(() => {
    if (!relatorioDados) return null;

    const totalTransacoes = relatorioDados.length;
    
    // FLUXO REAL DE CAIXA
    const entradaReal = relatorioDados
      .filter(t => t.fluxo_caixa === 'entrada')
      .reduce((sum, t) => sum + (t.valor || 0), 0);
    
    const saidaReal = relatorioDados
      .filter(t => t.fluxo_caixa === 'saida')
      .reduce((sum, t) => sum + (t.valor || 0), 0);
    
    // MOVIMENTAÇÃO INTERNA (cashback, etc)
    const movimentacaoInterna = relatorioDados
      .filter(t => t.fluxo_caixa === 'neutro')
      .reduce((sum, t) => sum + (t.valor || 0), 0);

    const saldoLiquido = entradaReal - saidaReal;

    // ESTATÍSTICAS DE STATUS
    const transacoesConcluidas = relatorioDados.filter(t => 
      t.status === 'completed' || t.status === 'confirmed'
    ).length;
    
    const transacoesPendentes = relatorioDados.filter(t => 
      t.status === 'pending' || t.status === 'pendente_confirmacao' || t.status === 'validating'
    ).length;
    
    const transacoesFalhas = relatorioDados.filter(t => 
      t.status === 'failed' || t.status === 'expired'
    ).length;

    const taxaSucesso = totalTransacoes > 0 ? (transacoesConcluidas / totalTransacoes) * 100 : 0;

    // ESTATÍSTICAS POR CATEGORIA
    const recebimentosPix = relatorioDados
      .filter(t => t.categoria_transacao === 'Recebimento PIX')
      .reduce((sum, t) => sum + (t.valor || 0), 0);

    const cashbacks = relatorioDados
      .filter(t => t.categoria_transacao === 'Cashback')
      .reduce((sum, t) => sum + (t.valor || 0), 0);

    const pagamentos = relatorioDados
      .filter(t => t.categoria_transacao === 'Pagamento Cliente')
      .reduce((sum, t) => sum + (t.valor || 0), 0);

    return {
      totalTransacoes,
      entradaReal,
      saidaReal,
      movimentacaoInterna,
      saldoLiquido,
      transacoesConcluidas,
      transacoesPendentes,
      transacoesFalhas,
      taxaSucesso,
      recebimentosPix,
      cashbacks,
      pagamentos,
    };
  }, [relatorioDados]);

  // COLUNAS DA TABELA
  const colunasTabela = useMemo(() => [
    {
      titulo: 'Data/Hora', chave: 'data_hora_formatada',
      render: (transacao: RelatorioTransacaoExibicao) => (
        <span className="text-sm text-text-secondary font-medium">{transacao.data_hora_formatada}</span>
      ),
    },
    {
      titulo: 'Cliente', chave: 'nome_cliente',
      render: (transacao: RelatorioTransacaoExibicao) => (
        <span className="font-medium text-text-light">{transacao.nome_cliente}</span>
      ),
    },
    {
      titulo: 'Categoria', chave: 'categoria_transacao',
      render: (transacao: RelatorioTransacaoExibicao) => (
        <Badge 
          tipo={
            transacao.categoria_transacao === 'Recebimento PIX' ? 'success' :
            transacao.categoria_transacao === 'Cashback' ? 'warning' :
            transacao.categoria_transacao === 'Pagamento Cliente' ? 'primary' : 'secondary'
          } 
          className="text-xs"
        >
          {transacao.categoria_transacao}
        </Badge>
      ),
    },
    {
      titulo: 'Origem/Destino', chave: 'origem_destino',
      render: (transacao: RelatorioTransacaoExibicao) => (
        <span className="text-text-secondary text-sm">{transacao.origem_destino}</span>
      ),
    },
    {
      titulo: 'Valor', chave: 'valor_formatado',
      render: (transacao: RelatorioTransacaoExibicao) => (
        <span className={`font-bold ${
          transacao.fluxo_caixa === 'entrada' ? 'text-green-500' : 
          transacao.fluxo_caixa === 'saida' ? 'text-red-500' : 'text-yellow-500'
        }`}>
          {transacao.fluxo_caixa === 'entrada' ? '+' : 
           transacao.fluxo_caixa === 'saida' ? '-' : ''}{transacao.valor_formatado}
        </span>
      ),
    },
    {
      titulo: 'Status', chave: 'status',
      render: (transacao: RelatorioTransacaoExibicao) => (
        <Badge tipo={transacao.status_color as any} className="text-xs">
          {determinarLabelStatus(transacao.status || '')}
        </Badge>
      ),
    },
    {
      titulo: 'Fluxo', chave: 'fluxo_caixa',
      render: (transacao: RelatorioTransacaoExibicao) => (
        <Badge 
          tipo={transacao.fluxo_caixa === 'entrada' ? 'success' : transacao.fluxo_caixa === 'saida' ? 'error' : 'warning'} 
          className="text-xs"
        >
          {transacao.fluxo_caixa === 'entrada' ? 'Entrada' : transacao.fluxo_caixa === 'saida' ? 'Saída' : 'Interno'}
        </Badge>
      ),
    },
    {
      titulo: 'Descrição', chave: 'descricao_detalhada',
      render: (transacao: RelatorioTransacaoExibicao) => (
        <span className="text-text-secondary text-sm">{transacao.descricao_detalhada}</span>
      ),
    },
  ], []);

  const carregando = carregandoClientes || carregandoTransacoes;

  return (
    <AdminLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="p-6 bg-background text-text min-h-screen"
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-primary mb-2">
            Relatórios de Transações
          </h1>
          <p className="text-text-secondary">
            Analise e exporte relatórios detalhados das transações do sistema
          </p>
        </div>

        {/* Filtros Aprimorados */}
        <Card className="mb-8 p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-2xl font-semibold text-secondary">Filtros do Relatório</h2>
              {filtrosAplicados > 0 && (
                <Badge tipo="primary" className="text-xs">
                  {filtrosAplicados} filtro{filtrosAplicados > 1 ? 's' : ''} aplicado{filtrosAplicados > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <div className="flex gap-2 mt-2 sm:mt-0">
              <Button
                onClick={handleLimparFiltros}
                tipo="secondary"
                className="text-sm"
              >
                🗑️ Limpar Tudo
              </Button>
              <Button
                onClick={() => setMostrarFiltrosAvancados(!mostrarFiltrosAvancados)}
                tipo="outline"
                className="text-sm"
              >
                {mostrarFiltrosAvancados ? '🙈 Ocultar' : '🔍 Avançados'}
              </Button>
            </div>
          </div>
          
          {/* FILTROS RÁPIDOS - PERÍODO PRÉ-DEFINIDO */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Período
              </label>
              <Select
                opcoes={opcoesPeriodo}
                value={periodoPredefinido}
                onValueChange={setPeriodoPredefinido}
                placeholder="Selecione o período"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Data de Início
              </label>
              <DatePicker
                selected={dataInicio}
                onChange={(date) => {
                  setDataInicio(date);
                  setPeriodoPredefinido('personalizado');
                }}
                placeholderText="Selecione a data de início"
                className="w-full"
                disabled={periodoPredefinido !== 'personalizado'}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Data de Fim
              </label>
              <DatePicker
                selected={dataFim}
                onChange={(date) => {
                  setDataFim(date);
                  setPeriodoPredefinido('personalizado');
                }}
                placeholderText="Selecione a data de fim"
                className="w-full"
                disabled={periodoPredefinido !== 'personalizado'}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Unidade
              </label>
              <Select
                opcoes={opcoesUnidades}
                value={unidadeSelecionadaId}
                onValueChange={setUnidadeSelecionadaId}
                placeholder="Selecione uma unidade"
                className="w-full"
              />
            </div>
          </div>

          {/* FILTROS BÁSICOS */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Cliente
              </label>
              <Select
                opcoes={opcoesClientes}
                value={clienteSelecionadoId}
                onValueChange={setClienteSelecionadoId}
                placeholder="Selecione um cliente"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Tipo de Operação
              </label>
              <Select
                opcoes={opcoesTipoOperacao}
                value={tipoOperacao}
                onValueChange={setTipoOperacao}
                placeholder="Selecione o tipo"
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Status
              </label>
              <Select
                opcoes={opcoesStatus}
                value={statusTransacao}
                onValueChange={setStatusTransacao}
                placeholder="Selecione o status"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text mb-2">
                Buscar
              </label>
              <Input
                type="text"
                value={buscarTexto}
                onChange={(e) => setBuscarTexto(e.target.value)}
                placeholder="Cliente, descrição, ID..."
                className="w-full"
              />
            </div>
          </div>

          {/* FILTROS AVANÇADOS COM ANIMAÇÃO */}
          <AnimatePresence>
            {mostrarFiltrosAvancados && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6 pt-6 border-t border-border">
                  <div>
                    <label className="block text-sm font-medium text-text mb-2">
                      Valor Mínimo
                    </label>
                    <Input
                      type="number"
                      value={valorMinimo}
                      onChange={(e) => setValorMinimo(e.target.value)}
                      placeholder="R$ 0,00"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text mb-2">
                      Valor Máximo
                    </label>
                    <Input
                      type="number"
                      value={valorMaximo}
                      onChange={(e) => setValorMaximo(e.target.value)}
                      placeholder="R$ 10.000,00"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text mb-2">
                      Categoria
                    </label>
                    <Select
                      opcoes={opcoesCategoria}
                      value={categoriaSelecionada}
                      onValueChange={setCategoriaSelecionada}
                      placeholder="Selecione a categoria"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text mb-2">
                      Fluxo de Caixa
                    </label>
                    <Select
                      opcoes={opcoesFluxoCaixa}
                      value={fluxoCaixaSelecionado}
                      onValueChange={setFluxoCaixaSelecionado}
                      placeholder="Selecione o fluxo"
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="flex justify-end mb-4">
                  <Button
                    onClick={handleLimparFiltrosAvancados}
                    tipo="outline"
                    className="text-sm"
                  >
                    🗑️ Limpar Avançados
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* RESUMO DO PERÍODO SELECIONADO */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center p-4 bg-background-dark rounded-lg">
            <div className="text-sm text-text-secondary">
              {dataInicio && dataFim && (
                <span>
                  Período selecionado: <strong>{format(dataInicio, 'dd/MM/yyyy')}</strong> até <strong>{format(dataFim, 'dd/MM/yyyy')}</strong>
                </span>
              )}
            </div>
            
            <div className="flex gap-4">
              <Button
                onClick={handleGerarRelatorio}
                disabled={isLoadingReport || carregando}
                tipo="primary"
                className="min-w-[140px]"
              >
                {isLoadingReport ? <Spinner size="sm" color="white" /> : '🔍 Aplicar Filtros'}
              </Button>
              
              {relatorioDados && relatorioDados.length > 0 && (
                <Button
                  onClick={handleExportCSV}
                  tipo="secondary"
                  className="min-w-[140px]"
                >
                  📊 Exportar CSV
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Alertas */}
        {errorReport && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Alert tipo="error" mensagem={errorReport} />
          </motion.div>
        )}

        {/* Loading */}
        {(isLoadingReport || carregando) && (
          <div className="flex justify-center items-center h-64">
            <Spinner size="lg" color="primary" />
            <span className="ml-4 text-text">
              {carregando ? 'Carregando dados...' : 'Aplicando filtros...'}
            </span>
          </div>
        )}

        {/* Conteúdo do Relatório */}
        {relatorioDados && relatorioDados.length > 0 && estatisticas && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {/* Estatísticas */}
            <Card className="p-6">
              <h2 className="text-2xl font-semibold text-secondary mb-6">Visão Geral - Fluxo Real</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <div className="text-2xl font-bold text-primary">{estatisticas.totalTransacoes}</div>
                  <div className="text-sm text-text-secondary">Total Transações</div>
                </div>
                <div className="text-center p-4 bg-green-500/10 rounded-lg">
                  <div className="text-2xl font-bold text-green-500">{formatCurrency(estatisticas.entradaReal)}</div>
                  <div className="text-sm text-text-secondary">Entradas Reais</div>
                </div>
                <div className="text-center p-4 bg-red-500/10 rounded-lg">
                  <div className="text-2xl font-bold text-red-500">{formatCurrency(estatisticas.saidaReal)}</div>
                  <div className="text-sm text-text-secondary">Saídas Reais</div>
                </div>
                <div className={`text-center p-4 rounded-lg ${
                  estatisticas.saldoLiquido >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'
                }`}>
                  <div className={`text-2xl font-bold ${
                    estatisticas.saldoLiquido >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {formatCurrency(estatisticas.saldoLiquido)}
                  </div>
                  <div className="text-sm text-text-secondary">Saldo Líquido</div>
                </div>
                <div className="text-center p-4 bg-green-500/10 rounded-lg">
                  <div className="text-2xl font-bold text-green-500">{estatisticas.transacoesConcluidas}</div>
                  <div className="text-sm text-text-secondary">Concluídas</div>
                </div>
                <div className="text-center p-4 bg-blue-500/10 rounded-lg">
                  <div className="text-2xl font-bold text-blue-500">{estatisticas.taxaSucesso.toFixed(1)}%</div>
                  <div className="text-sm text-text-secondary">Taxa Sucesso</div>
                </div>
              </div>
              
              {/* DETALHAMENTO ADICIONAL */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
                <div className="text-center p-4 bg-yellow-500/5 rounded-lg">
                  <div className="text-lg font-bold text-yellow-500">{estatisticas.transacoesPendentes}</div>
                  <div className="text-sm text-text-secondary">Pendentes</div>
                </div>
                <div className="text-center p-4 bg-red-500/5 rounded-lg">
                  <div className="text-lg font-bold text-red-500">{estatisticas.transacoesFalhas}</div>
                  <div className="text-sm text-text-secondary">Falhas/Expiradas</div>
                </div>
                <div className="text-center p-4 bg-yellow-500/5 rounded-lg">
                  <div className="text-lg font-bold text-yellow-500">{formatCurrency(estatisticas.movimentacaoInterna)}</div>
                  <div className="text-sm text-text-secondary">Mov. Interna</div>
                </div>
              </div>

              {/* DETALHAMENTO POR CATEGORIA */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
                <div className="text-center p-4 bg-green-500/5 rounded-lg">
                  <div className="text-lg font-bold text-green-500">{formatCurrency(estatisticas.recebimentosPix)}</div>
                  <div className="text-sm text-text-secondary">Recebimentos PIX</div>
                </div>
                <div className="text-center p-4 bg-yellow-500/5 rounded-lg">
                  <div className="text-lg font-bold text-yellow-500">{formatCurrency(estatisticas.cashbacks)}</div>
                  <div className="text-sm text-text-secondary">Cashbacks</div>
                </div>
                <div className="text-center p-4 bg-red-500/5 rounded-lg">
                  <div className="text-lg font-bold text-red-500">{formatCurrency(estatisticas.pagamentos)}</div>
                  <div className="text-sm text-text-secondary">Pagamentos</div>
                </div>
              </div>
            </Card>

            {/* Gráficos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card className="p-6">
                <h3 className="text-xl font-semibold text-secondary mb-4">Evolução Diária - Entradas</h3>
                <div className="h-120">
                  <DataChart
                    data={chartData}
                    title="Entradas por Dia"
                    chartType="line"
                    dataKeyX="name"
                    dataKeysY={["valor"]}
                    colors={["#10B981"]}
                  />
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-xl font-semibold text-secondary mb-4">Distribuição por Status</h3>
                <div className="h-120">
                  <DataChart
                    data={statusChartData}
                    title="Distribuição por Status"
                    chartType="pie"
                    pieValueKey="value"
                    pieNameKey="name"
                    colors={['#10B981', '#eab308', '#EF4444', '#8B5CF6', '#06B6D4', '#F59E0B']}
                  />
                </div>
              </Card>
            </div>

            {/* Tabela de Detalhes COM PAGINAÇÃO */}
            <Card className="p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h2 className="text-2xl font-semibold text-secondary">Detalhes das Transações</h2>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-2 sm:mt-0">
                  <span className="text-text-secondary text-sm">
                    {relatorioDados.length} registros encontrados
                  </span>
                  
                  {/* 🆕 CONTROLE DE ITENS POR PÁGINA */}
                  <div className="flex items-center gap-2">
                    <span className="text-text-secondary text-sm">Itens por página:</span>
                    <Select
                      opcoes={opcoesItensPorPagina}
                      value={itensPorPagina.toString()}
                      onValueChange={(value) => {
                        setItensPorPagina(Number(value));
                        setPaginaAtual(1);
                      }}
                      className="w-32"
                    />
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto mb-6">
                <Table 
                  colunas={colunasTabela} 
                  dados={dadosPaginados}
                  className="min-w-full"
                />
              </div>

              {/* 🆕 PAGINAÇÃO */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border">
                <div className="text-sm text-text-secondary">
                  Mostrando {inicioItem} a {fimItem} de {relatorioDados.length} registros
                </div>
                
                <div className="flex items-center gap-2">
                  {/* BOTÃO ANTERIOR */}
                  <Button
                    onClick={voltarPagina}
                    disabled={paginaAtual === 1}
                    tipo="outline"
                    className="px-3 py-2"
                  >
                    ‹ Anterior
                  </Button>

                  {/* INDICADOR DE PÁGINA */}
                  <div className="flex items-center gap-1 mx-2">
                    <span className="text-sm text-text-secondary">Página</span>
                    <span className="text-sm font-medium text-text">{paginaAtual}</span>
                    <span className="text-sm text-text-secondary">de</span>
                    <span className="text-sm font-medium text-text">{totalPaginas}</span>
                  </div>

                  {/* BOTÃO PRÓXIMO */}
                  <Button
                    onClick={avancarPagina}
                    disabled={paginaAtual === totalPaginas}
                    tipo="outline"
                    className="px-3 py-2"
                  >
                    Próximo ›
                  </Button>
                </div>

                {/* 🆕 NAVEGAÇÃO RÁPIDA PARA PÁGINAS ESPECÍFICAS (apenas se houver muitas páginas) */}
                {totalPaginas > 5 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-text-secondary">Ir para:</span>
                    <Input
                      type="number"
                      min="1"
                      max={totalPaginas}
                      value={paginaAtual}
                      onChange={(e) => {
                        const pagina = Number(e.target.value);
                        if (pagina >= 1 && pagina <= totalPaginas) {
                          irParaPagina(pagina);
                        }
                      }}
                      className="w-20"
                    />
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Estado Vazio */}
        {relatorioDados && relatorioDados.length === 0 && !isLoadingReport && !errorReport && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center h-64 rounded-lg bg-background-dark"
          >
            <div className="text-6xl mb-4">📊</div>
            <p className="text-xl text-text-secondary mb-2">Nenhuma transação encontrada</p>
            <p className="text-text-secondary text-center">
              {filtrosAplicados > 0 
                ? 'Tente ajustar os filtros para visualizar mais dados.'
                : 'Não há transações no período selecionado.'}
            </p>
          </motion.div>
        )}
      </motion.div>
    </AdminLayout>
  );
};

export default ReportsPage;